"use client"

import React, { useState, useEffect, useRef, createContext, useContext, ReactNode } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import {
  Copy, Check, Download, PanelRightClose, FileCode, PanelLeftClose, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"
import Editor, { useMonaco } from "@monaco-editor/react"
import { useUser } from "@/components/providers/user-context"
import { useTheme } from "next-themes"
import { useSidebar } from "@/components/layout/chat-sidebar"
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

// Create context for canvas state
interface CanvasContextType {
  isExpanded: boolean;
  toggleCanvas: () => void;
  setIsExpanded: (expanded: boolean) => void;
  isTablet: boolean;
}

export const CanvasContext = createContext<CanvasContextType>({
  isExpanded: false,
  toggleCanvas: () => { },
  setIsExpanded: () => { },
  isTablet: false,
});

export const useCanvas = () => useContext(CanvasContext);

interface CanvasProviderProps {
  children: ReactNode;
  defaultExpanded?: boolean;
}

export const CanvasProvider = ({ children, defaultExpanded = false }: CanvasProviderProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isTablet, setIsTablet] = useState(false);

  // Effect to check if device is tablet (between mobile and desktop)
  useEffect(() => {
    const checkTablet = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 768 && width <= 1024); // Between md and lg breakpoints
    };

    checkTablet();
    window.addEventListener('resize', checkTablet);

    return () => window.removeEventListener('resize', checkTablet);
  }, []);

  const toggleCanvas = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <CanvasContext.Provider value={{ isExpanded, toggleCanvas, setIsExpanded, isTablet }}>
      {children}
    </CanvasContext.Provider>
  );
};

export interface CanvasFile {
  id: string
  name: string
  content: string
  language?: string
}

interface CanvasProps {
  files: CanvasFile[]
  defaultExpanded?: boolean
  className?: string
  readOnly?: boolean
  onChange?: (id: string, value: string) => void
  activeFileId?: string
}

export const Canvas: React.FC<CanvasProps> = ({
  files = [],
  defaultExpanded = true,
  className,
  readOnly = true,
  onChange,
  activeFileId,
}) => {
  const [activeTab, setActiveTab] = useState<string>(activeFileId || files[0]?.id || "")
  const [openFiles, setOpenFiles] = useState<CanvasFile[]>(files)
  const [isCopied, setIsCopied] = useState<boolean>(false)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const monaco = useMonaco()
  const editorRef = useRef<any>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const { preferences } = useUser()
  const { resolvedTheme } = useTheme()

  // Use the canvas context instead of local state
  const { isExpanded, toggleCanvas, setIsExpanded, isTablet } = useCanvas()

  // Import sidebar context to coordinate states
  const { isOpen, toggleSidebar } = useSidebar();

  // Track if device is mobile for responsive behavior
  const [isMobile, setIsMobile] = useState(false);

  // Set initial expanded state based on props
  useEffect(() => {
    if (defaultExpanded !== undefined) {
      setIsExpanded(defaultExpanded);
    }
  }, [defaultExpanded, setIsExpanded]);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is standard md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Effect to coordinate canvas and sidebar states
  useEffect(() => {
    // If canvas is opened, close the sidebar
    if (isExpanded && isOpen) {
      toggleSidebar();
    }
  }, [isExpanded, isOpen, toggleSidebar]);

  // Custom toggle function to close sidebar when opening canvas
  const handleToggleCanvas = () => {
    // If we're going to open the canvas, ensure sidebar is closed
    if (!isExpanded && isOpen) {
      toggleSidebar();
    }
    // Perform the regular toggle
    toggleCanvas();
  };

  // Map user font size preference to Monaco font size
  const fontSizeMap: Record<string, number> = {
    small: 12,
    medium: 14,
    large: 18,
  }
  const fontSize = fontSizeMap[preferences.fontSize || "medium"] || 14

  // Setup Monaco editor when instance is available
  useEffect(() => {
    if (monaco) {
      // Configure Monaco editor as needed
      monaco.languages.typescript.javascriptDefaults.setEagerModelSync(true)

      // Create custom themes that match the website's theme
      monaco.editor.defineTheme('website-light', {
        base: 'vs',
        inherit: true,
        rules: [
          { token: 'comment', foreground: '6A737D' },
          { token: 'keyword', foreground: 'D73A49' },
          { token: 'string', foreground: '032F62' },
          { token: 'number', foreground: '005CC5' },
          { token: 'operator', foreground: '24292E' },
          { token: 'variable', foreground: '6F42C1' },
          { token: 'function', foreground: '6F42C1' },
          { token: 'type', foreground: '005CC5' }
        ],
        colors: {
          'editor.background': '#ffffff',
          'editor.foreground': '#24292e',
          'editor.lineHighlightBackground': '#f6f8fa',
          'editorLineNumber.foreground': '#1b1f23',
          'editorLineNumber.activeForeground': '#24292e',
          'editor.selectionBackground': '#0366d625',
          'editor.inactiveSelectionBackground': '#0366d610',
          'editor.findMatchBackground': '#ffdf5d66',
          'editor.findMatchHighlightBackground': '#ffdf5d33',
          'editorCursor.foreground': '#24292e'
        }
      })

      monaco.editor.defineTheme('website-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'comment', foreground: '8B949E' },
          { token: 'keyword', foreground: 'FF7B72' },
          { token: 'string', foreground: 'A5D6FF' },
          { token: 'number', foreground: '79C0FF' },
          { token: 'operator', foreground: 'C9D1D9' },
          { token: 'variable', foreground: 'D2A8FF' },
          { token: 'function', foreground: 'D2A8FF' },
          { token: 'type', foreground: '79C0FF' }
        ],
        colors: {
          'editor.background': '#0a0b0f',
          'editor.foreground': '#f8fafc',
          'editor.lineHighlightBackground': '#1e293b',
          'editorLineNumber.foreground': '#64748b',
          'editorLineNumber.activeForeground': '#f8fafc',
          'editor.selectionBackground': '#3b82f640',
          'editor.inactiveSelectionBackground': '#3b82f620',
          'editor.findMatchBackground': '#3b82f660',
          'editor.findMatchHighlightBackground': '#3b82f630',
          'editorCursor.foreground': '#f8fafc'
        }
      })

      // Set Monaco theme based on website color scheme
      if (resolvedTheme === "dark") {
        monaco.editor.setTheme("website-dark")
      } else {
        monaco.editor.setTheme("website-light")
      }
    }
  }, [monaco, resolvedTheme])

  useEffect(() => {
    if (files.length > 0) {
      // `filterUniqueFiles` uses the current `activeTab` state to determine which version of a file to show.
      const newOpenFiles = filterUniqueFiles(files);
      setOpenFiles(newOpenFiles);

      // Determine the correct activeTab based on props and the newOpenFiles.
      // Priority:
      // 1. activeFileId prop (if valid within newOpenFiles)
      // 2. Current activeTab (if valid within newOpenFiles and not empty)
      // 3. First file in newOpenFiles
      // 4. Empty string if no files
      let newActiveTabTarget = "";
      if (activeFileId && newOpenFiles.some(f => f.id === activeFileId)) {
        newActiveTabTarget = activeFileId;
      } else if (activeTab && newOpenFiles.some(f => f.id === activeTab)) {
        // Current activeTab is still valid and present in the newOpenFiles
        newActiveTabTarget = activeTab;
      } else if (newOpenFiles.length > 0) {
        newActiveTabTarget = newOpenFiles[0].id;
      }
      // No 'else' needed for newActiveTabTarget = "", it's the default from initialization

      if (activeTab !== newActiveTabTarget) {
        setActiveTab(newActiveTabTarget);
      }

    } else { // files.length is 0
      setOpenFiles([]);
      if (activeTab !== "") { // Only update if it's not already cleared
        setActiveTab("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, activeFileId]); // Dependencies are `files` and `activeFileId` (props).
                              // `activeTab` is intentionally omitted from deps for this part.
                              // `filterUniqueFiles` uses `activeTab` from state.
                              // The eslint-disable is for `activeTab` used in the logic but not in deps.

  // Helper function to group files by name for version management
  const groupFilesByName = (files: CanvasFile[]) => {
    const groups: Record<string, CanvasFile[]> = {};

    files.forEach(file => {
      if (!groups[file.name]) {
        groups[file.name] = [];
      }
    });

    return groups;
  };

  // Helper function to filter files to only show one instance per name
  const filterUniqueFiles = (files: CanvasFile[]) => {
    const uniqueFiles: CanvasFile[] = [];
    const filesByName: Record<string, CanvasFile[]> = {};

    // Group files by name first
    files.forEach(file => {
      if (!filesByName[file.name]) {
        filesByName[file.name] = [];
      }
      filesByName[file.name].push(file);
    });

    // For each group of files with the same name
    Object.keys(filesByName).forEach(fileName => {
      const filesWithName = filesByName[fileName];

      // First priority: Check if one of these is the active tab
      const activeFile = filesWithName.find(f => f.id === activeTab);
      if (activeFile) {
        // If there's an active file with this name, use it
        uniqueFiles.push(activeFile);
      } 
      // Second priority: Check if one of the files is already in openFiles
      else {
        const existingOpenFile = openFiles.find(of => 
          filesWithName.some(f => f.id === of.id)
        );
        
        if (existingOpenFile && files.some(f => f.id === existingOpenFile.id)) {
          // If there's already a file with this name in openFiles, keep using that one
          uniqueFiles.push(files.find(f => f.id === existingOpenFile.id)!);
        } else {
          // Otherwise, use the latest version (last in the array)
          uniqueFiles.push(filesWithName[filesWithName.length - 1]);
        }
      }
    });

    return uniqueFiles;
  };

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor
  }

  const handleCopy = () => {
    if (editorRef.current) {
      const value = editorRef.current.getValue()
      navigator.clipboard.writeText(value)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    }
  }

  const handleEditorChange = (value: string | undefined) => {
    if (onChange && value !== undefined && activeTab) {
      onChange(activeTab, value)
    }
  }

  const closeTab = (id: string) => {
    // Get the name of the file being closed
    const fileBeingClosed = openFiles.find(f => f.id === id);
    if (!fileBeingClosed) return;

    // Filter out the file from openFiles
    const updatedFiles = openFiles.filter(f => f.id !== id);
    setOpenFiles(updatedFiles);

    // If we closed the active tab, activate another file with the same name if available,
    // otherwise activate the first remaining tab
    if (id === activeTab) {
      const sameNameFile = files.find(f => f.name === fileBeingClosed.name && f.id !== id);
      if (sameNameFile && updatedFiles.some(f => f.id === sameNameFile.id)) {
        setActiveTab(sameNameFile.id);
      } else if (updatedFiles.length > 0) {
        setActiveTab(updatedFiles[0].id);
      } else {
        setActiveTab(""); // Clear activeTab if no files are left
      }
    }
  }

  // If there are no open files or if on mobile, don't render the canvas
  if (openFiles.length === 0 || isMobile) {
    // Reset expanded state when no tabs are open or on mobile
    if (isExpanded) setIsExpanded(false)
    return null
  }

  const activeFile = openFiles.find(f => f.id === activeTab)

  const downloadAllFiles = async () => {
    setIsDownloading(true)
    try {
      const zip = new JSZip()

      // Add all open files to the zip
      openFiles.forEach(file => {
        // Get the most up-to-date content for the active file from the editor
        const content = file.id === activeTab && editorRef.current
          ? editorRef.current.getValue()
          : file.content

        zip.file(file.name, content)
      })

      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' })

      // Create a name for the zip file based on the first file or the current date
      const zipFileName = activeFile
        ? `${activeFile.name.split('.')[0]}-files.zip`
        : `code-files-${new Date().toISOString().split('T')[0]}.zip`

      // Download the zip file using FileSaver
      saveAs(zipBlob, zipFileName)
    } catch (error) {
      console.error('Error creating zip file:', error)
      // Fallback to downloading files one by one if zip creation fails
      openFiles.forEach(file => {
        const content = file.id === activeTab && editorRef.current
          ? editorRef.current.getValue()
          : file.content

        const blob = new Blob([content], { type: 'text/plain' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = file.name
        document.body.appendChild(a)
        a.click()

        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      })
    } finally {
      setIsDownloading(false)
    }
  }
  return (
    <>
      {/* Canvas toggle button - only show on non-mobile and when not on tablet with expanded canvas */}
      {!isMobile && !(isTablet && isExpanded) && (
        <Button
          variant="outline"
          size="icon"
          className={cn(
            "canvas-toggle-button fixed top-20 z-50 transition-all duration-300 ease-in-out bg-opacity-80 backdrop-blur-sm text-foreground shadow-md border",
            isExpanded
              ? "right-[calc(60%+20px)] md:right-[calc(50%+20px)]"
              : "right-2",
            isTablet && isExpanded && "opacity-0 pointer-events-none"
          )}
          style={{ borderColor: 'hsl(var(--primary))' }}
          onClick={handleToggleCanvas}
        >
          {isExpanded ? <PanelRightClose className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      )}
      {/* Main canvas container - only shown on non-mobile */}
      <div
        ref={canvasRef}
        className={cn(
          "fixed z-40 transition-all duration-300 ease-in-out overflow-hidden bg-background text-foreground border-border",
          isExpanded ? "border shadow-lg" : "border-none",
          isExpanded
            ? (isTablet
              ? "right-[0.5rem] top-[calc(4rem+1rem)] bottom-[0.5rem] w-[90%] rounded-xl"
              : "right-[0.5rem] top-[calc(4rem+1rem)] bottom-[0.5rem] w-[60%] md:w-[49%] rounded-xl")
            : "right-[0.5rem] top-[calc(4rem+1rem)] bottom-[0.5rem] w-0",
          `font-size-${preferences.fontSize || 'medium'}`,
          className
        )}
      style={{ borderColor: 'hsl(var(--primary))' }}
      >
        {/* Rest of the canvas UI... */}
        <div className="flex flex-col h-full">
          {/* Modern header with file name and toolbar */}
          <div className={cn(
            "flex items-center justify-between p-2.5 px-3 bg-card text-card-foreground",
            isTablet && isExpanded && "mt-0"
          )}>
            {/* File name display with icon */}
            <div className="flex items-center gap-1 overflow-x-auto max-w-[60vw] hide-scrollbar">
              {openFiles.map((file) => (
                <button
                  key={file.id}
                  className={cn(
                    "flex items-center px-3 py-1.5 rounded-t-md text-sm font-medium transition-colors mr-1 min-w-[90px] max-w-[180px] truncate border",
                    file.id === activeTab
                      ? "bg-background text-primary border-l border-t border-r border-b-0 z-10 relative"
                      : "bg-muted text-muted-foreground border-l-0 border-t-0 border-r-0 border-transparent hover:bg-card hover:text-foreground z-0",
                    "relative group"
                  )}
                  onClick={() => setActiveTab(file.id)}
                  style={{ outline: 'none' }}
                >
                  <FileCode className="h-4 w-4 min-w-4 mr-1" />
                  <span className="truncate flex-1">{file.name}</span>
                  {/* Close button for tab */}
                  {openFiles.length > 1 && (
                    <span
                      className="ml-2 opacity-60 hover:opacity-100 cursor-pointer text-xs"
                      onClick={e => {
                        e.stopPropagation();
                        const fileIdToClose = file.id; // current file in map
                        const updatedOpenFiles = openFiles.filter(f => f.id !== fileIdToClose);
                        setOpenFiles(updatedOpenFiles);

                        if (fileIdToClose === activeTab) {
                          if (updatedOpenFiles.length > 0) {
                            setActiveTab(updatedOpenFiles[0].id);
                          } else {
                            setActiveTab(""); // Clear activeTab if no files are left
                          }
                        }
                      }}
                      title="Close tab"
                    >
                      ×
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Actions toolbar */}
            <div className="flex items-center gap-1">
              {/* Copy button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-muted"
                onClick={handleCopy}
                title="Copy code"
              >
                {isCopied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>

              {/* Download button */}
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-8 w-8 rounded-full hover:bg-muted",
                  isDownloading && "opacity-50 cursor-not-allowed"
                )}
                onClick={downloadAllFiles}
                disabled={isDownloading}
                title="Download code"
              >
                {isDownloading ? (
                  <div className="h-4 w-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>

            </div>
          </div>
          {/* Monaco Editor with improved styling */}
          <div className="flex-1 overflow-hidden">
            {activeFile && (
              <div className="h-full w-full p-0 bg-background">
                <Editor
                  height="100%"
                  width="100%"
                  language={activeFile.language || 'javascript'}
                  value={activeFile.content}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    scrollbar: {
                      vertical: 'visible',
                      horizontal: 'visible',
                      verticalScrollbarSize: 8,
                      horizontalScrollbarSize: 8,
                    },
                    lineNumbers: 'on',
                    renderLineHighlight: 'all',
                    theme: resolvedTheme === "dark" ? "website-dark" : "website-light",
                    fontFamily: "'SF Mono', Menlo, Monaco, 'Courier New', monospace",
                    fontSize,
                    fontLigatures: true,
                    padding: { top: 16 },
                    smoothScrolling: true,
                    cursorBlinking: "smooth",
                    // Disable syntax error highlighting
                    quickSuggestions: { other: false, comments: false, strings: false },
                    parameterHints: { enabled: false },
                    suggestOnTriggerCharacters: false,
                    acceptSuggestionOnEnter: "off",
                    tabCompletion: "off",
                    wordBasedSuggestions: "currentDocument",
                    occurrencesHighlight: "off",
                    renderValidationDecorations: "off"
                  }}
                  onMount={handleEditorDidMount}
                  onChange={handleEditorChange}
                />
              </div>
            )}
          </div>
          {/* Modern file info footer */}
          {activeFile && (
            <div className="px-4 py-2 border-t text-xs flex justify-between items-center border-border text-muted-foreground bg-card">              {/* Version Dropdown (now on the left) */}
              <div> 
                {((): React.ReactNode => {
                  // Only execute if activeFile is defined
                  if (!activeFile) return null;
                  
                  const sameNameFiles = files.filter((f: CanvasFile) => f.name === activeFile.name);
                  if (sameNameFiles.length > 1) {
                    const currentVersionIndex = sameNameFiles.findIndex((f: CanvasFile) => f.id === activeFile.id);
                    const versionNumber = currentVersionIndex !== -1 ? currentVersionIndex + 1 : "?";
                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-xs">
                            v{versionNumber} of {sameNameFiles.length} <ChevronUp className="h-3 w-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start"> {/* Align dropdown content to the start */}
                          {sameNameFiles.map((versionFile: CanvasFile, index: number) => (
                            <DropdownMenuItem
                              key={versionFile.id}
                              onClick={() => {
                                // First, find the current file in openFiles
                                const currentFileIndex = openFiles.findIndex(f => f.name === activeFile.name);
                                // Create a copy of the openFiles array
                                const newOpenFiles = [...openFiles];
                                
                                // If we found the current file with the same name, replace it with the selected version
                                if (currentFileIndex !== -1) {
                                  newOpenFiles[currentFileIndex] = versionFile;
                                  setOpenFiles(newOpenFiles);
                                }
                                
                                // Set the active tab to the selected version
                                setActiveTab(versionFile.id);
                              }}
                              className={cn(activeFile.id === versionFile.id && "bg-muted")}
                            >
                              v{index + 1}{activeFile.id === versionFile.id ? " (current)" : ""}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  }
                  return null; 
                })()}
              </div>

              {/* Right side: File size and other info */}
              <div className="flex items-center gap-2">
                <div>
                  {activeFile.language || 'plaintext'}
                </div>
                <div>|</div>
                <div>
                  {activeFile.content.length} bytes
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}